import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Typography, Tooltip, Alert } from 'antd';
import { ReloadOutlined, ToolOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTranslationWithNamespace } from '../../hooks/useTranslationWithNamespace';
import { showSuccessNotification, showErrorNotification } from '../../utils/notificationUtils';
import { fetchCircuitBreakers, resetCircuitBreaker, resetAllCircuitBreakers } from '../../api/health';

const { Title, Text } = Typography;

interface CircuitBreakerPanelProps {
  refreshInterval?: number;
}

const CircuitBreakerPanel: React.FC<CircuitBreakerPanelProps> = ({ refreshInterval = 30000 }) => {
  const { t: commonT } = useTranslation();
  const { t: queueT } = useTranslationWithNamespace('queue');
  const [circuitBreakers, setCircuitBreakers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch circuit breakers
  const fetchData = async (bypassCache = false) => {
    try {
      setLoading(true);
      console.log(`Fetching circuit breakers (bypassCache: ${bypassCache})`);
      const response = await fetchCircuitBreakers(bypassCache);

      // Handle empty or missing response
      if (!response || !response.circuitBreakers) {
        console.warn('Circuit breakers response is empty or missing circuitBreakers array');
        setCircuitBreakers([]);
        setError('No circuit breakers found. This may be due to a configuration issue.');
      } else {
        setCircuitBreakers(response.circuitBreakers);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching circuit breakers:', err);
      setError('Failed to fetch circuit breakers. Please check the server logs.');
      setCircuitBreakers([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and refresh interval
  useEffect(() => {
    // Initial fetch
    fetchData();

    // Only set up interval if refreshInterval is positive
    let intervalId: NodeJS.Timeout | null = null;
    if (refreshInterval > 0) {
      intervalId = setInterval(fetchData, refreshInterval);
      console.log(`Circuit breaker refresh interval set: ${refreshInterval}ms`);
    }

    // Cleanup function
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
        console.log('Circuit breaker refresh interval cleared');
      }
    };
  }, [refreshInterval]);

  // Handle reset all circuit breakers
  const handleResetAll = async () => {
    try {
      const response = await resetAllCircuitBreakers();

      if (response && response.success) {
        showSuccessNotification(
          queueT('circuitBreaker.resetSuccess', 'Circuit Breakers Reset'),
          queueT('circuitBreaker.resetAllSuccess', 'All circuit breakers have been reset successfully')
        );
      } else {
        showErrorNotification(
          queueT('circuitBreaker.resetError', 'Reset Failed'),
          queueT('circuitBreaker.resetAllError', 'Failed to reset all circuit breakers')
        );
      }

      // Refresh data after reset with bypass cache
      fetchData(true);
    } catch (err) {
      console.error('Error resetting all circuit breakers:', err);
      showErrorNotification(
        queueT('circuitBreaker.resetError', 'Reset Failed'),
        queueT('circuitBreaker.resetAllError', 'Failed to reset all circuit breakers')
      );
    }
  };

  // Handle reset specific circuit breaker
  const handleReset = async (name: string, type: string) => {
    try {
      const response = await resetCircuitBreaker(name, type);

      if (response && response.success) {
        showSuccessNotification(
          queueT('circuitBreaker.resetSuccess', 'Circuit Breaker Reset'),
          queueT('circuitBreaker.resetSpecificSuccess', 'Circuit breaker {{name}} has been reset successfully', {
            name,
            interpolation: { escapeValue: false }
          })
        );
      } else {
        showErrorNotification(
          queueT('circuitBreaker.resetError', 'Reset Failed'),
          queueT('circuitBreaker.resetSpecificError', 'Failed to reset circuit breaker {{name}}', {
            name,
            interpolation: { escapeValue: false }
          })
        );
      }

      // Refresh data after reset with bypass cache
      fetchData(true);
    } catch (err) {
      console.error(`Error resetting circuit breaker ${name}:`, err);
      showErrorNotification(
        queueT('circuitBreaker.resetError', 'Reset Failed'),
        queueT('circuitBreaker.resetSpecificError', 'Failed to reset circuit breaker {{name}}', {
          name,
          interpolation: { escapeValue: false }
        })
      );
    }
  };

  // Format circuit breaker name for display
  const formatName = (name: string) => {
    if (name.includes(':')) {
      const [type, operation] = name.split(':');
      return `${operation} (${type.toLowerCase()})`;
    }
    return name;
  };

  // Extract operation name from full circuit breaker name
  const extractOperationName = (name: string) => {
    if (name.includes(':')) {
      return name.split(':')[1];
    }
    return name;
  };

  // Extract operation type from full circuit breaker name
  const extractOperationType = (name: string) => {
    if (name.includes(':')) {
      return name.split(':')[0];
    }
    return 'QUEUE'; // Default to QUEUE if no type is specified
  };

  // Table columns
  const columns = [
    {
      title: queueT('circuitBreaker.name', 'Name'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{formatName(text)}</Text>
    },
    {
      title: queueT('circuitBreaker.state', 'State'),
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => {
        let color = 'green';
        if (state === 'OPEN') color = 'red';
        if (state === 'HALF_OPEN') color = 'orange';

        return <Tag color={color}>{state}</Tag>;
      }
    },
    {
      title: queueT('circuitBreaker.failureCount', 'Failures'),
      dataIndex: 'metrics',
      key: 'failureCount',
      render: (metrics: any) => metrics?.failedCalls || 0
    },
    {
      title: queueT('circuitBreaker.successCount', 'Successes'),
      dataIndex: 'metrics',
      key: 'successCount',
      render: (metrics: any) => metrics?.successfulCalls || 0
    },
    {
      title: queueT('circuitBreaker.lastError', 'Last Error'),
      dataIndex: 'metrics',
      key: 'lastError',
      render: (metrics: any) => {
        if (!metrics?.lastError) return '-';

        return (
          <Tooltip title={metrics.lastError}>
            <Text ellipsis style={{ maxWidth: 200 }}>{metrics.lastError}</Text>
          </Tooltip>
        );
      }
    },
    {
      title: queueT('circuitBreaker.type', 'Type'),
      dataIndex: 'metrics',
      key: 'type',
      render: (metrics: any) => metrics?.operationType || '-'
    },
    {
      title: commonT('actions.title', 'Actions'),
      key: 'actions',
      render: (_: any, record: any) => (
        <Space size="small">
          <Button
            size="small"
            icon={<ToolOutlined />}
            onClick={() => handleReset(extractOperationName(record.name), extractOperationType(record.name))}
            type={record.state === 'OPEN' ? 'primary' : 'default'}
            danger={record.state === 'OPEN'}
          >
            {queueT('circuitBreaker.reset', 'Reset')}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <Card
      title={<Title level={4}>{queueT('circuitBreaker.title', 'Circuit Breakers')}</Title>}
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchData(true)}
            loading={loading}
          >
            {commonT('actions.refresh', 'Refresh')}
          </Button>
          <Button
            icon={<ToolOutlined />}
            onClick={handleResetAll}
            type="primary"
          >
            {queueT('circuitBreaker.resetAll', 'Reset All')}
          </Button>
        </Space>
      }
    >
      {error && (
        <Alert
          message={queueT('error.title', 'Error')}
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {circuitBreakers.length > 0 ? (
        <Table
          dataSource={circuitBreakers.map(cb => ({ ...cb, key: cb.name }))}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      ) : (
        <Alert
          message={queueT('circuitBreaker.noCircuitBreakers', 'No Circuit Breakers Found')}
          description={queueT('circuitBreaker.noCircuitBreakersDescription', 'No circuit breakers have been registered in the system. You may need to initialize them by running the initialize-circuit-breakers.js script.')}
          type="info"
          showIcon
        />
      )}
    </Card>
  );
};

export default CircuitBreakerPanel;
